import React, { useEffect, useState } from "react";
import { api, formatError } from "../../api";
import { X, Plus, Trash2, Calendar, Video, Phone, MapPin, Loader2, Sparkles } from "lucide-react";

const MODES = [
  { key: "video", label: "Video", icon: Video },
  { key: "phone", label: "Phone", icon: Phone },
  { key: "onsite", label: "Onsite", icon: MapPin },
];

function isoLocal(date) {
  // Convert a Date to "YYYY-MM-DDTHH:mm" suitable for <input type="datetime-local">
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
}

/**
 * InterviewSchedulerModal — propose an interview round (initial or any next round).
 *
 * Backend: POST /api/applications/{appId}/interview/round
 * Calls onScheduled(interview) on success.
 */
export default function InterviewSchedulerModal({
  appId,
  candidateName = "the candidate",
  defaultRoundName = "Tech round 1",
  onClose,
  onScheduled,
}) {
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
  const [roundName, setRoundName] = useState(defaultRoundName);
  const [mode, setMode] = useState("video");
  const [meetingLink, setMeetingLink] = useState("");
  const [venue, setVenue] = useState("");
  const [interviewerName, setInterviewerName] = useState("");
  const [interviewerEmail, setInterviewerEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [slots, setSlots] = useState([isoLocal(tomorrow)]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [autoTeams, setAutoTeams] = useState(false);
  const [teamsStatus, setTeamsStatus] = useState(null);  // {configured: bool}

  useEffect(() => {
    api.get("/integrations/ms-teams/status")
      .then((r) => setTeamsStatus(r.data))
      .catch(() => setTeamsStatus({ configured: false }));
  }, []);

  const updateSlot = (i, v) => {
    setSlots((arr) => arr.map((s, idx) => (idx === i ? v : s)));
  };
  const addSlot = () => {
    if (slots.length >= 5) return;
    setSlots((arr) => [...arr, isoLocal(new Date(Date.now() + (arr.length + 1) * 24 * 60 * 60 * 1000))]);
  };
  const removeSlot = (i) => {
    setSlots((arr) => (arr.length <= 1 ? arr : arr.filter((_, idx) => idx !== i)));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    const cleanSlots = slots.map((s) => s?.trim?.() || s).filter(Boolean);
    if (!roundName.trim()) { setErr("Round name is required."); return; }
    if (cleanSlots.length === 0) { setErr("Add at least one slot."); return; }
    setBusy(true); setErr("");
    try {
      const body = {
        round_name: roundName.trim(),
        slots: cleanSlots.map((s) => new Date(s).toISOString()),
        mode,
      };
      if (mode === "video" && meetingLink.trim()) body.meeting_link = meetingLink.trim();
      if (mode === "video" && !meetingLink.trim() && autoTeams) body.auto_teams = true;
      if (mode === "onsite" && venue.trim()) body.venue = venue.trim();
      if (interviewerName.trim()) body.interviewer_name = interviewerName.trim();
      if (interviewerEmail.trim()) body.interviewer_email = interviewerEmail.trim();
      if (notes.trim()) body.notes = notes.trim();

      const r = await api.post(`/applications/${appId}/interview/round`, body);
      onScheduled?.(r.data);
      onClose?.();
    } catch (e2) {
      setErr(formatError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="interview-scheduler-modal">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <form onSubmit={submit} className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <header className="p-5 border-b flex items-start justify-between gap-3" style={{ borderColor: "var(--rule)" }}>
          <div>
            <div className="eyebrow inline-flex items-center gap-1.5"><Calendar size={11} /> Schedule round</div>
            <h2 className="font-display font-extrabold text-xl mt-1">Propose interview slots</h2>
            <p className="text-xs text-muted mt-1">Send up to 5 slots to <strong>{candidateName}</strong> — they pick one to confirm.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-md hover:bg-paper grid place-items-center" data-testid="scheduler-close">
            <X size={18} />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {err && <div className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2" data-testid="scheduler-error">{err}</div>}

          <div>
            <label className="eyebrow">Round label</label>
            <input
              className="input mt-1.5"
              value={roundName}
              onChange={(e) => setRoundName(e.target.value)}
              placeholder="Tech round 1 / Manager / HR Final…"
              data-testid="scheduler-round-name"
              maxLength={60}
              required
            />
          </div>

          <div>
            <label className="eyebrow">Mode</label>
            <div className="mt-1.5 grid grid-cols-3 gap-2">
              {MODES.map((m) => {
                const Icon = m.icon;
                const active = mode === m.key;
                return (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setMode(m.key)}
                    data-testid={`scheduler-mode-${m.key}`}
                    className="px-3 py-2 rounded-lg border text-sm font-semibold inline-flex items-center justify-center gap-2 transition"
                    style={{
                      borderColor: active ? "var(--signal)" : "var(--rule)",
                      color: active ? "var(--signal)" : "var(--ink)",
                      background: active ? "rgb(0 108 255 / 0.08)" : "transparent",
                    }}
                  >
                    <Icon size={14} /> {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          {mode === "video" && (
            <div>
              <label className="eyebrow inline-flex items-center gap-1.5">
                Meeting link
                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "rgba(70, 65, 220, 0.10)", color: "#4641DC" }}>
                  Teams · Meet · Zoom
                </span>
              </label>
              <input
                className="input mt-1.5"
                value={meetingLink}
                onChange={(e) => { setMeetingLink(e.target.value); if (e.target.value) setAutoTeams(false); }}
                placeholder="https://teams.microsoft.com/l/meetup-join/…"
                data-testid="scheduler-meeting-link"
                disabled={autoTeams && !meetingLink}
              />
              <div className="text-[11px] text-muted mt-1">
                Tip: in Microsoft Teams, click <b>Calendar → New meeting</b>, save, then open the
                event and click <b>Copy join link</b>. Paste it here.
              </div>
              {teamsStatus?.configured && (
                <label className="mt-3 flex items-start gap-2.5 p-3 rounded-lg cursor-pointer hover:bg-paper transition-colors border" style={{ borderColor: autoTeams ? "#4641DC" : "var(--rule)", background: autoTeams ? "rgba(70,65,220,0.05)" : "transparent" }}>
                  <input type="checkbox" checked={autoTeams} onChange={(e) => { setAutoTeams(e.target.checked); if (e.target.checked) setMeetingLink(""); }} className="mt-0.5" data-testid="scheduler-auto-teams" />
                  <span className="text-xs">
                    <span className="font-bold inline-flex items-center gap-1"><Sparkles size={11} style={{ color: "#4641DC" }} /> Auto-generate a Microsoft Teams meeting</span>
                    <span className="block text-muted mt-0.5">
                      We&apos;ll create a Teams meeting via Microsoft Graph and email the candidate the join link. Skips this if a link is pasted above.
                    </span>
                  </span>
                </label>
              )}
              {teamsStatus && !teamsStatus.configured && (
                <div className="mt-3 text-[11px] text-muted bg-paper rounded-md px-3 py-2 border border-rule" data-testid="scheduler-teams-not-configured">
                  Microsoft Teams auto-generation is not configured yet. Ask your admin to wire <code className="font-mono">MS_TENANT_ID</code> / <code className="font-mono">MS_CLIENT_ID</code> / <code className="font-mono">MS_CLIENT_SECRET</code>.
                </div>
              )}
            </div>
          )}
          {mode === "onsite" && (
            <div>
              <label className="eyebrow">Venue address</label>
              <input
                className="input mt-1.5"
                value={venue}
                onChange={(e) => setVenue(e.target.value)}
                placeholder="Office address / floor / room"
                data-testid="scheduler-venue"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow">Interviewer name</label>
              <input
                className="input mt-1.5"
                value={interviewerName}
                onChange={(e) => setInterviewerName(e.target.value)}
                placeholder="Priya Sharma"
                data-testid="scheduler-interviewer-name"
              />
            </div>
            <div>
              <label className="eyebrow">Interviewer email</label>
              <input
                type="email"
                className="input mt-1.5"
                value={interviewerEmail}
                onChange={(e) => setInterviewerEmail(e.target.value)}
                placeholder="priya@company.com"
                data-testid="scheduler-interviewer-email"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="eyebrow">Available slots ({slots.length}/5)</label>
              {slots.length < 5 && (
                <button type="button" onClick={addSlot} className="text-xs font-semibold text-signal inline-flex items-center gap-1" data-testid="scheduler-add-slot">
                  <Plus size={12} /> Add slot
                </button>
              )}
            </div>
            <div className="mt-1.5 space-y-2">
              {slots.map((s, i) => (
                <div key={`slot-${i}`} className="flex items-center gap-2">
                  <input
                    type="datetime-local"
                    className="input flex-1"
                    value={s}
                    onChange={(e) => updateSlot(i, e.target.value)}
                    required
                    data-testid={`scheduler-slot-${i}`}
                  />
                  {slots.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSlot(i)}
                      aria-label="Remove slot"
                      data-testid={`scheduler-remove-slot-${i}`}
                      className="w-9 h-9 grid place-items-center text-muted hover:text-red-600 hover:bg-red-50 rounded-md transition"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="eyebrow">Notes for the candidate (optional)</label>
            <textarea
              className="input mt-1.5 min-h-[80px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What to expect, what to prepare, any docs to bring…"
              maxLength={1000}
              data-testid="scheduler-notes"
            />
          </div>
        </div>

        <footer className="p-5 border-t flex items-center justify-end gap-2" style={{ borderColor: "var(--rule)" }}>
          <button type="button" onClick={onClose} className="btn-secondary" data-testid="scheduler-cancel">
            Cancel
          </button>
          <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="scheduler-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Calendar size={14} />}
            {busy ? "Sending…" : "Send slots"}
          </button>
        </footer>
      </form>
    </div>
  );
}
